import React, { FC, useCallback, useState } from 'react';
import { FormHelperText, IconButton, InputAdornment, InputLabel, OutlinedInput } from '@mui/material';
import Clear from '@mui/icons-material/Clear';
import { ButtonForInput } from '../../UI/styled';
import { redColor } from '../../../@theme/vars';
import { MATERIAL_URL_CONSTS } from './MaterialIncutUrl.consts';
import { FormControlFlex, MainBox } from './MaterialIncutUrl.styles';

type MaterialUrlProps = {
  setUrl: (url: string) => void;
  handleAddMaterial: () => void;
  setDataLoaded?: (loaded: boolean) => void;
  dataLoaded?: boolean;
  blockCount: number;
};

const MaterialIncutUrl: FC<MaterialUrlProps> = ({ setUrl, handleAddMaterial, setDataLoaded, dataLoaded, blockCount }) => {
  const [currentUrl, setCurrentUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);

  const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/i;

  const validateUrl = useCallback((url: string): boolean => {
    if (!url) {
      setUrlError(null); 
      return false;
    }
    const isValid = urlRegex.test(url);
    setUrlError(isValid ? null : MATERIAL_URL_CONSTS.invalidUrlMsg);
    return isValid;
  }, []);

  const isButtonDisabled = !currentUrl || !!urlError;

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setCurrentUrl(value);
      setUrl(value);
      validateUrl(value);
    },
    [setUrl, validateUrl]
  );

  const handleClearInput = useCallback(() => {
    setCurrentUrl('');
    setUrl('');
    setUrlError(null);
  }, [setUrl]);

  const handleAddClick = useCallback(() => {
    if (validateUrl(currentUrl)) {
      handleAddMaterial();
      setDataLoaded?.(false);
      setCurrentUrl('');
      setUrl('');
      setUrlError(null);
    }
  }, [currentUrl, handleAddMaterial, setDataLoaded, setUrl, validateUrl]);

  return (
    <MainBox>
      <FormControlFlex error={!!urlError}>
        <InputLabel>{MATERIAL_URL_CONSTS.labelMsg}</InputLabel>
        <OutlinedInput
          label={MATERIAL_URL_CONSTS.labelMsg}
          onChange={handleInputChange}
          value={currentUrl}
          error={!!urlError}
          endAdornment={
            <InputAdornment position="end">
              {currentUrl && (
                <IconButton edge="end" size="small" onClick={handleClearInput}>
                  <Clear fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          }
        />
        {urlError && <FormHelperText sx={{ color: redColor }}>{urlError}</FormHelperText>}
      </FormControlFlex>
      <ButtonForInput
        variant="outlined"
        color="primary"
        disabled={isButtonDisabled}
        onClick={handleAddClick}
      >
        {MATERIAL_URL_CONSTS.addMsg}
      </ButtonForInput>
    </MainBox>
  );
};

export default MaterialIncutUrl;